import React, { createContext, useContext, useEffect } from "react";
import { useConfirmDialog, useDialog, useGlobalStore, useSnackbars } from "@hooks";
import {
  BookingKitFormConfigFragment,
  BookingKitProfileFragment,
  CreateBookingKitInput,
  TimeReportKit_GetInfo,
  TimeReportTemplateType,
  useBookingKit_GetChecksLazyQuery,
  useBookingKit_GetFormConfigLazyQuery,
  useBookingProfile_GetTagsQuery,
  BookingChecks,
  useTimereport_GetDetailsLazyQuery,
  useTimeReportKit_CreateBookingKitMutation,
  useTimeReportKit_DeleteBookingKitMutation,
  useTimeReportKit_GetInfoLazyQuery,
  useTimeReportKit_UpdateBookingKitMutation,
  BookingKit_GetUserPermissions,
  TimeReport_GetBookingInfo
} from "@graphql";
import { useForm } from "react-hook-form";
import { FormData, initialValues, isEmptyTR } from "./utils";
import { UseFormReturn } from "react-hook-form";
import { BookingSupportStatusDto } from "@bonliva-crm/timereport-api-client-js";
import { translations } from "@bookings/BookingProfile/TimeReport/components/BookingSupportChecks";
import { OkCancelDialogOptions } from "@containers";
import locale from "@bonliva-crm/locale";
import { getOptions } from "@bookings/BookingProfile/BookingSupportTag/BookingSupportTagForm";
import { Option } from "@common/Select";

type TStore = {
  dialog: ReturnType<typeof useDialog<BookingKitProfileFragment>>;
  data?: TimeReportKit_GetInfo.Return | null;
  form: UseFormReturn<FormData>;
  handleOpen: (
    kit: BookingKitProfileFragment | null,
    templateType: TimeReportTemplateType,
    tags?: string[],
    comment?: string
  ) => void;
  loading: boolean;
  bookingId: string;
  onSubmit?: () => void;
  checkData: BookingChecks | null;
  formConfigData: BookingKitFormConfigFragment[];
  canDelete: boolean;
  handleDelete?: () => void;
  handleCLose?: () => void;
  invoicePaymentStatuses: BookingSupportStatusDto;
  isEmptyTR: boolean;
  tagsOptions: Option[];
  getUserPermissions: (
    userId: string
  ) => Promise<{ permissions: string[]; managerId: string | null }>;
  permissionsLoading: boolean;
};

type BookingKitFormReturnType = {
  store: TStore;
};

const BookingKitFormContext = createContext<BookingKitFormReturnType>(null!);

export const useBookingKitFormConsumer = () => {
  const context = useContext(BookingKitFormContext);

  if (context == null) {
    throw new Error(
      "useBookingKitFormContext must be used within BookingKitFormProvider"
    );
  }

  return context;
};

type Props = {
  bookingId: string;
  consultantId: string;
  children: React.ReactNode;
};

export const BookingKitFormProvider = ({ consultantId, bookingId, children }: Props) => {
  const dialog = useDialog<BookingKitProfileFragment | null>();
  const { user } = useGlobalStore();
  const confirm = useConfirmDialog();
  const snackbars = useSnackbars();
  const [checksValid, setChecksValid] = React.useState<boolean>(false);
  const [permissionsLoading, setPermissionsLoading] = React.useState<boolean>(false);

  const form = useForm<FormData>({
    // resolver: yupResolver(formCreateSchema)
    defaultValues: initialValues,
    mode: "all"
  });

  const [getTimereportDetails, { data: trData, loading: trLoading }] =
    useTimereport_GetDetailsLazyQuery({
      variables: { bookingId },
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        if (data?.timereport?.data) {
          // form.setValue("withTimeReport", !isEmptyTR(data.timereport.data));
        }
        if (data?.invoicePaymentStatuses?.data) {
          form.setValue(
            "withConsultantInvoice",
            data.invoicePaymentStatuses.data.paymentFormExists
          );
          form.setValue(
            "withCustomerInvoice",
            data.invoicePaymentStatuses.data.invoiceFormExists
          );
          form.setValue("withSickPay", data.invoicePaymentStatuses.data.sickFormExists);
          form.setValue(
            "withSubcontractorPay",
            data.invoicePaymentStatuses.data.subcontractorFormExists
          );
        }
      }
    });

  const [getConfig, { data: formConfigData, loading: configLoading }] =
    useBookingKit_GetFormConfigLazyQuery({
      variables: { bookingId },
      fetchPolicy: "network-only"
    });

  const [getChecks, { data: checkData, loading: checkLoading }] =
    useBookingKit_GetChecksLazyQuery({
      variables: {
        bookingId
      },
      onCompleted: (data) => {
        const errors = Object.entries(data.getChecksForBooking).filter(
          ([key, value]) => key !== "__typename" && value !== null
        );
        if (errors?.length > 0) {
          form.setValue(
            "comment",
            errors.map((x) => `${translations[x[0]]} - ${x[1]}`).join(",\r\n")
          );

          setChecksValid(false);
        } else {
          setChecksValid(true);
        }
      }
    });

  const [getKitInfo, { data, loading }] = useTimeReportKit_GetInfoLazyQuery({
    variables: {
      bookingId,
      consultantId
    },
    fetchPolicy: "network-only"
  });

  const getUserPermissions = async (userId: string) => {
    setPermissionsLoading(true);
    return BookingKit_GetUserPermissions.func({ userId }, { fetchPolicy: "network-only" })
      .then((response) => {
        return {
          permissions: response.data.userPermissions,
          managerId: response.data.userById.ManagerId
        };
      })
      .finally(() => {
        setPermissionsLoading(false);
      });
  };

  // initialize the dialog data
  useEffect(() => {
    if (dialog.open) {
      getKitInfo();
      getChecks();
      getTimereportDetails();
      getConfig();
    }
  }, [dialog.open]);

  const { data: tagsData } = useBookingProfile_GetTagsQuery({
    fetchPolicy: "cache-first"
  });

  const [updateBookingKit, { loading: isUpdating }] =
    useTimeReportKit_UpdateBookingKitMutation();
  const [createBookingKit, { loading: isCreating }] =
    useTimeReportKit_CreateBookingKitMutation();
  const [deleteBookingKit, { loading: isDeleting }] =
    useTimeReportKit_DeleteBookingKitMutation();

  const handleOpen = (
    kit: BookingKitProfileFragment | null,
    timeReportType: TimeReportTemplateType,
    tags?: string[],
    comment?: string
  ) => {
    dialog.onOpen(kit);

    form.reset({
      ...initialValues,
      timeReportType,
      type: kit?.type || null,
      managerId: kit?.manager?._id || null,
      tags: kit?.bookingSupportTagStatus?.map((x) => x.tag) || tags || [],
      notes: kit?.bookingSupportTagComment || comment || "",
      responsibleId: kit?.responsible?._id
    });
  };

  const getInput = (formData: FormData): CreateBookingKitInput => {
    const { checkedIds, receipts, timeReportType, notes, ...rest } = formData;
    return {
      ...rest,
      bookingId,
      consultantId,
      templateType: timeReportType,
      bookingSupportCheckWasValid: checksValid,
      withConsultantInvoice: Boolean(formData.withConsultantInvoice),
      withCustomerInvoice: Boolean(formData.withCustomerInvoice),
      withSickPay: Boolean(formData.withSickPay),
      withSubcontractorPay: Boolean(formData.withSubcontractorPay),
      withTimeReport: Boolean(formData.withTimeReport),
      filesIds: Object.keys(checkedIds).filter((x) => checkedIds[x]),
      receipts: Object.keys(receipts)
        .filter((x) => receipts[x].checked)
        .map((x) => receipts[x].value),
      tags: formData.tags,
      tagsNote: notes
    };
  };

  const onSubmit = async () => {
    const _input = getInput(form.getValues());
    // if no kitId then create createBookingKit
    if (!dialog.data?._id) {
      createBookingKit({
        variables: { input: _input },
        refetchQueries: [
          TimeReportKit_GetInfo.OperationName,
          TimeReport_GetBookingInfo.OperationName
        ]
      })
        .then(() => {
          snackbars.showSuccess(locale.Created);
          handleCLose();
        })
        .catch((err) => {
          snackbars.showError(`Error: ${err}`);
          console.error(err);
        });
    } else {
      updateBookingKit({
        variables: { input: { ..._input, id: dialog.data._id } },
        refetchQueries: [
          TimeReportKit_GetInfo.OperationName,
          TimeReport_GetBookingInfo.OperationName
        ]
      })
        .then(() => {
          snackbars.showSuccess(locale.Updated);
          handleCLose();
        })
        .catch((err) => {
          snackbars.showError(`Error: ${err}`);
          console.error(err);
        });
    }
  };

  const handleDelete = async () => {
    if (!dialog.data?._id) return;

    confirm.confirm(new OkCancelDialogOptions()).then((v) => {
      if (v !== "ok") {
        return;
      }

      deleteBookingKit({
        variables: { id: dialog.data._id },
        refetchQueries: [TimeReportKit_GetInfo.OperationName]
      })
        .then(() => {
          snackbars.showSuccess(locale.Deleted);
        })
        .catch((err) => {
          snackbars.showError(`Error deleting: ${err}`);
          console.error(err);
        });
    });
  };

  const canDelete = (dialog.data?._id && user.can("booking-kit:delete:all")) ?? false;

  const handleCLose = () => {
    form.reset(initialValues);
    setChecksValid(false);
    dialog.onClose();
  };

  // Initialize your store here, if needed
  return (
    <BookingKitFormContext.Provider
      value={{
        store: {
          dialog,
          form,
          handleOpen,
          loading:
            loading ||
            isUpdating ||
            isCreating ||
            configLoading ||
            checkLoading ||
            trLoading ||
            isDeleting,
          data,
          invoicePaymentStatuses: trData?.invoicePaymentStatuses.data ?? null,
          isEmptyTR: isEmptyTR(trData?.timereport.data ?? null),
          onSubmit,
          bookingId,
          checkData: checkData?.getChecksForBooking ?? null,
          formConfigData: formConfigData?.bookingKitFormConfig ?? [],
          canDelete,
          handleDelete,
          handleCLose,
          tagsOptions: getOptions(
            dialog?.data?.bookingSupportTagStatus,
            tagsData?.config,
            "bookingKit"
          ),
          permissionsLoading,
          getUserPermissions
        }
      }}
    >
      {children}
    </BookingKitFormContext.Provider>
  );
};
